/**
 * The Nótur tab with annotations: the sheet viewer with per-page annotation overlays and connector
 * pins (view and edit mode), plus — in edit mode ("Skrifa") — the floating toolbar (over the sheet,
 * which keeps the whole area), the connector flow, selection actions, text box, symbol palette,
 * layer panel and first-run coach marks. The banners keep clear of the toolbar on the side it is
 * docked to (CSS --annot-top / -bottom / -left / -right).
 */
import { LoaderCircle, TriangleAlert } from 'lucide-react';
import { memo, useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useToast } from '../components/toast';
import type { SongFileDTO } from '../../shared/dto/library';
import { useT } from '../i18n';
import { SheetPane } from '../viewer/SheetPane';
import type { RenderPageOverlay } from '../viewer/types';
import { CoachMarks } from './CoachMarks';
import { ConnectorLabelSheet, ConnectorTargetBanner } from './ConnectorFlow';
import { AnnotateContext } from './context';
import { LayerPanel, StaleBanner } from './LayerPanel';
import { PageOverlay } from './PageOverlay';
import { SelectionBar } from './SelectionBar';
import { SymbolPalette } from './SymbolPalette';
import { TextEditor } from './TextEditor';
import { Toolbar, type ToolbarInsets } from './Toolbar';
import { useAnnotate } from './useAnnotate';

/** Left / right of a banner across the sheet: clear of the toolbar when it stands at a side. */
const SIDE_INSETS: CSSProperties = { left: 'var(--annot-left, 0px)', right: 'var(--annot-right, 0px)' };

/** Props of the viewer stay referentially stable, so annotation edits never re-render it. */
const MemoSheetPane = memo(SheetPane);

function EngineStatusPill({ status }: { status: 'loading' | 'ready' | 'error' }) {
  const t = useT();
  if (status === 'ready') return null;
  return (
    <div className="pointer-events-none absolute z-30 flex justify-center px-3" style={{ ...SIDE_INSETS, top: 'calc(var(--annot-top, 0px) + 3.5rem)' }}>
      <span
        role="status"
        className="inline-flex items-center gap-2 rounded-full bg-[var(--surface)] px-4 py-2 text-[16px] font-medium shadow-lg ring-1 ring-[var(--line)]"
      >
        {status === 'loading' ? (
          <LoaderCircle className="size-5 animate-spin text-[var(--muted)]" aria-hidden />
        ) : (
          <TriangleAlert className="size-5 text-[var(--danger-text)]" aria-hidden />
        )}
        {status === 'loading' ? t('annotate.loading') : t('annotate.load_error')}
      </span>
    </div>
  );
}

export function SheetWorkspace({
  file,
  active,
  editing,
  onStaleRevision,
  onDone,
}: {
  file: SongFileDTO;
  active: boolean;
  editing: boolean;
  onStaleRevision: () => void;
  /** "Lokið" on the floating toolbar (the song page's top bar is hidden while writing). */
  onDone?: () => void;
}) {
  const ann = useAnnotate(file, editing);
  const [insets, setInsets] = useState<ToolbarInsets>({ top: 0, bottom: 0, left: 0, right: 0 });
  const insetVars = (
    editing
      ? { '--annot-top': `${insets.top}px`, '--annot-bottom': `${insets.bottom}px`, '--annot-left': `${insets.left}px`, '--annot-right': `${insets.right}px` }
      : undefined
  ) as CSSProperties | undefined;
  useUnsavedLocallyWarning(ann.engine.sync.unsavedLocally === true);
  const renderPageOverlay = useCallback<RenderPageOverlay>((page, size) => <PageOverlay page={page} w={size.w} h={size.h} />, []);

  return (
    <AnnotateContext.Provider value={ann.ctx}>
      <div className="flex h-full flex-col">
        <div className="relative min-h-0 flex-1" style={insetVars}>
          <MemoSheetPane
            file={file}
            active={active}
            onStaleRevision={onStaleRevision}
            mode={editing ? 'edit' : 'view'}
            connectors={ann.pins}
            renderPageOverlay={renderPageOverlay}
            viewerRef={ann.viewerRef}
            pinsInteractive={ann.pinsInteractive}
            onPinTap={ann.onPinTap}
            editTouchAction={ann.editTouchAction}
            onPageChange={ann.setCurrentPage}
          />
          {editing ? (
            <>
              <ConnectorTargetBanner ann={ann} />
              <SelectionBar ann={ann} />
              <EngineStatusPill status={ann.engine.status} />
              <Toolbar ann={ann} onDone={onDone} onInsets={setInsets} />
            </>
          ) : null}
          {/* Shown in view mode too: members who only read need to know marks may not fit a new version. */}
          {ann.staleVisible && !(editing && (ann.flow || ann.selected)) ? (
            <div className="pointer-events-none absolute z-20 px-3" style={{ ...SIDE_INSETS, bottom: 'calc(var(--annot-bottom, var(--tabs-bottom, 0px)) + 0.75rem)' }}>
              <div className="pointer-events-auto mx-auto max-w-xl shadow-lg">
                <StaleBanner ann={ann} onClose={ann.dismissStale} />
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {editing ? (
        <>
          <TextEditor ann={ann} />
          <SymbolPalette open={ann.paletteOpen} initial={ann.prefs.stamp} recent={ann.recentGlyphs} onConfirm={ann.confirmGlyph} onClose={ann.closePalette} />
          <ConnectorLabelSheet ann={ann} />
          <LayerPanel ann={ann} />
          {ann.coachOpen ? <CoachMarks onClose={ann.closeCoach} /> : null}
        </>
      ) : null}
    </AnnotateContext.Provider>
  );
}

/**
 * The device could not store annotation edits (IndexedDB write failed — storage full, private mode):
 * they exist only in memory until synced. Tell the singer once per occurrence, sticky, so they
 * connect before closing the app.
 */
function useUnsavedLocallyWarning(active: boolean) {
  const toast = useToast();
  const t = useT();
  const shown = useRef<number | null>(null);
  useEffect(() => {
    if (active && shown.current === null) {
      shown.current = toast.show(t('annotate.unsaved_locally'), { tone: 'error', sticky: true });
    } else if (!active && shown.current !== null) {
      toast.dismiss(shown.current);
      shown.current = null;
    }
  }, [active, toast, t]);
}
